import type { RiskAssessmentViewModel, TaskViewModel } from '@trinnvis/dabih-api-client';
import { BASE_PATH } from '@trinnvis/dabih-api-client';
import {
  assetsNotDeleted,
  commonFunctionUuid,
  contactsNotDeleted,
  currentEmployeeShortName,
  currentUserUuid,
  employeesAndContactsOptions,
  employeesNotDeleted,
  employeesShortNamesAndI,
  employeesShortNamesAndMe,
  eventDoneText,
  events,
  functionsNotDeleted,
  getAssetOptions,
  getContactsAsUsers,
  getModelEvents,
  getOrganization,
  groupBy,
  isNewEventFromSuggestion,
  key,
  riskAssessmentsNotDeleted,
  singleUserVersion,
  tasksNotDeleted,
  writeAccess,
} from 'src/store/selectors';
import type { State } from 'src/store/types';
import { LocalDate } from 'src/utilities/local-date';
import { isEmpty, sortBy } from 'lodash';
import { reminderText, dateTimeDescription } from 'src/store/utilities';
import { displayName } from 'src/utilities/text';
import type { ApiClient } from 'src/store/backend';
import { getRelatedCollections } from 'src/store/selectors/collections';
import { getPromoDataForEntity } from 'src/store/selectors/promo';
import type { ListSectionItemInput } from '../../library/lists/listSectionItemInput';
import type { CommonDataEntityView } from '../../content/common-data-entity-view';
import type { EventRelatedAsset } from '../../content/event-occurrences/event-related-asset';
import type { EventOccurrenceView } from '../../content/event-occurrences/event-occurrence-view';
import type { EventOccurrenceViewEditItem } from '../../content/event-occurrences/event-occurrence-view-edit-item';
import { assertIsDefined } from '../../assertIsDefined';
import { assertEntityFound } from './entity-not-found';
import { toAttachmentItems } from '../to-attachment-items';
import { fetchDraft } from '../fetch-draft';
import { getFeatureStates } from '../../store/selectors/features/getFeatureStates';
import { eventAssigneesText } from '../../store/eventAssigneesText';
import { functionNameWithAssignedNames } from '../../store/functionNameWithAssignedNames';
import { shortenPath } from 'src/utilities/path';
import { pageIds } from 'src/store/selectors/pages/pageIds';
import { t } from 'src/i18n';

export function getMessageRecipients(state: State, selectedEmployees: string[] = []) {
  const employees = employeesNotDeleted(state)
    .filter((p) => p.status !== 'TERMINATED' || selectedEmployees.includes(p.uuid))
    .filter((p) => p.uuid !== currentUserUuid(state))
    .map((p) => ({ uuid: p.uuid, name: p.name, accessLevel: p.accessLevel?.toString() ?? 'NONE' }));
  const contacts = contactsNotDeleted(state)
    .filter((p) => p.uuid !== currentUserUuid(state))
    .map((p) => ({
      uuid: p.uuid,
      name: p.firstName + ' ' + p.lastName,
      accessLevel: p.accessLevel?.toString() ?? 'NONE',
    }));
  return employees.concat(contacts);
}

function getAssetListItems(items: EventRelatedAsset[], href: string, eventUuid: string) {
  return items.map((item) => ({
    writeAccess: true,
    label: item.name,
    notes: item.notes,
    assetCheck: item.assetCheck,
    assetCheckStatus: item.assetCheckStatus,
    assetCheckDoneText: item.assetCheckDoneText,
    assetCheckComments: item.assetCheckComments,
    href: href + '/assets/' + item.uuid,
    accessible: true,
    clickData: JSON.stringify({ eventOccurrenceUuid: eventUuid, assetUuid: item.uuid }),
  }));
}

function getRelatedAssetsAsListItems(
  assets: EventRelatedAsset[],
  href: string,
  eventUuid: string,
): ListSectionItemInput[] {
  const hasLocation = assets.find((a) => {
    return a.location && a.location !== '';
  });
  if (hasLocation) {
    const grouped = groupBy(assets, (a) => a.location);
    const located: ListSectionItemInput[] = grouped
      .filter((g) => {
        return g.group && g.group !== '';
      })
      .map((g) => {
        return {
          writeAccess: true,
          updateStatus: 'none',
          label: g.group,
          accessible: false,
          items: getAssetListItems(g.items, href, eventUuid),
        };
      });
    const unlocated: ListSectionItemInput[] = grouped
      .filter((g) => {
        return !g.group || g.group === '';
      })
      .map((g) => {
        return {
          writeAccess: true,
          updateStatus: 'none',
          label: t('content.locationNotSpecified'),
          accessible: false,
          items: getAssetListItems(g.items, href, eventUuid),
        };
      });

    return located.concat(unlocated);
  }
  return getAssetListItems(assets, href, eventUuid);
}

function getRelatedRiskAssessments(
  riskAssessments: RiskAssessmentViewModel[],
  uuid: string,
  baseHref: string,
): { name: string; href: string }[] {
  return riskAssessments
    .filter((riskAssessment) => {
      return riskAssessment.relatedEvents?.includes(uuid) && riskAssessment.name !== undefined;
    })
    .map((riskAssessment) => {
      return {
        name: riskAssessment.name ?? '',
        href: shortenPath(baseHref + '/riskAssessments/' + riskAssessment.uuid) ?? '',
      };
    });
}

export function buildEventOccurrenceView(
  api: ApiClient,
  uuid: string,
  commonData: CommonDataEntityView,
  state: State,
  currentParent: string,
): EventOccurrenceView {
  const organization = getOrganization(state);
  assertIsDefined(organization);

  const eventOccurrence = organization.eventOccurrences.find((o) => o.uuid === uuid);

  assertEntityFound(eventOccurrence, 'eventOccurrences', uuid);

  const instance = eventOccurrence.date;

  const entity = eventOccurrence;

  const assets = assetsNotDeleted(state);
  const relatedAssetViewModels = eventOccurrence.relatedAssets ?? [];
  const relatedAssets: EventRelatedAsset[] = relatedAssetViewModels
    .map((a) => {
      const relatedAsset = assets.find((x) => x.uuid === a.assetUuid);
      return {
        asset: a,
        relatedAsset: relatedAsset,
      };
    })
    .filter((x) => x.relatedAsset !== undefined)
    .map((x) => {
      const relatedAsset = x.relatedAsset;
      assertIsDefined(relatedAsset);
      let name = relatedAsset.name + ' ' + relatedAsset.number || t('content.withoutName');
      if (relatedAsset.discardedYear !== '') {
        name += t('content.assetDiscardedSuffix', { year: relatedAsset.discardedYear });
      }
      const a = x.asset;
      return {
        name,
        location: relatedAsset.location ?? '',
        notes: '',
        assetCheck: true,
        assetCheckStatus: a.assetStatus ?? 'NONE',
        assetCheckDoneText: _checkedByTime(
          state,
          a.assetStatus ?? 'NONE',
          a.comments ?? '',
          a.statusLastUpdatedByEmployeeUuid ?? '',
          a.statusLastUpdatedAt ?? '',
        ),
        assetCheckComments: a.comments ?? '',
        href: '/account/' + organization.organizationId + '/110/assets/' + relatedAsset.uuid,
        uuid: a.assetUuid,
      };
    });

  const item = events(state).find((x) => x.eventOccurrences.find((y) => y.uuid === uuid) !== undefined);
  assertEntityFound(item, 'eventOccurrences', uuid);

  let taskUuid = entity.taskUuid ?? '';
  let startTaskUuid: string | undefined = undefined;
  let task: TaskViewModel | undefined = undefined;
  if (taskUuid) {
    task = organization.tasksById[taskUuid];
    if (task) {
      if (task.locked) {
        startTaskUuid = task.uuid;
      }
    } else {
      taskUuid = '';
    }
  }

  const localDate = LocalDate.fromString(entity.date);

  const canUpdateFutureOccurrences =
    entity.isConfirmedEntity &&
    !isEmpty(entity.date) &&
    item.eventOccurrences.filter((e2) => LocalDate.fromString(e2.date).isAfter(localDate)).length > 0;

  let relatedAssetForNew = '';
  if (!entity.isConfirmedEntity && relatedAssets.length) {
    relatedAssetForNew = relatedAssets[0].uuid;
  }

  const availableFunctions = functionsNotDeleted(state)
    .filter((f) => {
      return f.isConfirmedEntity;
    })
    .map((f) => {
      return {
        value: f.uuid,
        text: functionNameWithAssignedNames(f.uuid, functionsNotDeleted(state), employeesShortNamesAndI(state)),
        employees: f.employees,
      };
    });

  const taskOptions = tasksNotDeleted(state)
    .filter((t) => {
      return t.name !== undefined && t.name !== '';
    })
    .map((t) => {
      return { value: t.uuid, text: t.name || '' };
    });

  const doneByOptions = employeesAndContactsOptions(state, entity.employees);

  let doneBy = '';
  if (entity.doneByEmployeeUuid) {
    doneBy = entity.doneByEmployeeUuid;
  }
  if (entity.doneUpdatedByContactPersonUuid) {
    doneBy = entity.doneUpdatedByContactPersonUuid;
  }

  function isEmergencyContent() {
    return (
      riskAssessmentsNotDeleted(state).find((r) => {
        return r.pages?.includes(pageIds.emergency) && r.relatedEvents?.includes(uuid);
      }) !== undefined
    );
  }

  return {
    ...commonData,
    promoData: getPromoDataForEntity('eventOccurrences', item, getFeatureStates(state)),
    modelEvents: getModelEvents(state, relatedAssetForNew),
    docsForLinking: [],
    currentUserHasAccess: true,
    currentUserHasWriteAccess: writeAccess(state),
    isConfirmedEntity: entity.isConfirmedEntity,
    uuid: uuid,
    eventUuid: uuid,
    eventInstance: instance,
    helpContent: '', // TODO entity.helpContent ?? '',
    href: currentParent + '/eventOccurrences/' + uuid,
    pdfHref:
      BASE_PATH +
      '/organizations/' +
      organization.organizationId +
      '/eventOccurrences/' +
      uuid +
      '.pdf?key=' +
      key(state),
    parentHref: currentParent,
    deleted: false,
    reminderText: reminderText(entity.reminder ?? 'NONE'),
    assigneeDisplayText: eventAssigneesText(
      entity,
      functionsNotDeleted(state),
      employeesShortNamesAndMe(state),
      getContactsAsUsers(state),
    ),
    notes: entity.notes ?? '',
    scheduleDescription: '', // entity.scheduleDescription,
    timeDescription: dateTimeDescription(entity.date ?? '', entity.time ?? '', entity.durationMinutes),
    type: 'eventOccurrences',
    name: displayName(entity.name, ''),
    attachments: toAttachmentItems(api, 'eventOccurrences', entity.uuid, entity.attachments),
    doneDate: entity.doneDate ?? '',
    doneLabel: entity.doneDate ? t('assets.legendDone') : t('events.notDone'),
    doneText: eventDoneText(entity, doneByOptions, organization.singleUser, true),
    hasAssets: relatedAssets.length > 0,
    thisEventInstance: '',
    defaultEmployee: '',
    defaultFunction: '',
    today: LocalDate.fromString(state.today),
    relatedAssets: relatedAssets,
    relatedAssetsAsListItems: getRelatedAssetsAsListItems(
      relatedAssets,
      currentParent + '/eventOccurrences/' + uuid,
      uuid,
    ),
    availableEmployees: sortBy(
      employeesShortNamesAndMe(state)
        .filter((e) => e.name !== '')
        .filter((e) => e.status !== 'TERMINATED' || (entity.employees ?? []).includes(e.uuid))
        .map((p) => ({ value: p.uuid, text: p.name })),
      ['text'],
    ),
    availableContacts: sortBy(
      getContactsAsUsers(state)
        .filter((p) => p.name !== '')
        .map((p) => ({ value: p.uuid, text: p.name + ' (' + p.partnerName + ')' })),
      ['text'],
    ),
    doneByOptions,
    doneBy,
    availableFunctions: sortBy(availableFunctions, ['text']),
    commonFunctionUuid: commonFunctionUuid(state),
    currentUserUuid: currentUserUuid(state),
    currentEmployeeShortName: currentEmployeeShortName(state),
    messageRecipients: getMessageRecipients(state, entity.employees ?? []),
    deletable: true,
    hasDraft: eventOccurrence.hasDraft,
    fetchDraft: async () => {
      const command = await fetchDraft(api, 'eventOccurrences', uuid, organization.organizationId);
      return command.draft as EventOccurrenceViewEditItem;
    },
    assetOptions: getAssetOptions(state, relatedAssets),
    newItem: !entity.isConfirmedEntity,
    procedures: entity.description,
    taskHref: task === undefined ? '' : currentParent + '/eventOccurrences/' + uuid + '/tasks/' + task.uuid,
    taskUuid,
    taskName: task?.name ?? '',
    startTaskUuid,
    eventType: 'standard',
    date: entity.date ?? '',
    time: entity.time ?? 'NONE',
    durationMinutes: entity.durationMinutes,
    persistent: entity.persistent,
    assignedToEmployees: entity.employees ?? [],
    assignedToFunction: entity.functionUuid ?? '',
    assignedToContacts: entity.contacts ?? [],
    reminder: entity.reminder ?? 'NONE',
    occurrences: sortBy(
      item.eventOccurrences
        .filter((o) => {
          return o.isConfirmedEntity;
        })
        .map((o) => {
          let dateTime = o.date ?? '';
          if (o.time) {
            dateTime += ' ' + o.time;
          }
          let assignees = '';
          if (!singleUserVersion(state)) {
            assignees = eventAssigneesText(
              o,
              functionsNotDeleted(state),
              employeesShortNamesAndI(state),
              getContactsAsUsers(state),
            );
          }
          return {
            occurrenceUuid: o.uuid,
            dateTime,
            classified: false,
            restricted: false,
            displayDate: dateTimeDescription(o.date ?? '', o.time ?? '', o.durationMinutes),
            durationMinutes: o.durationMinutes,
            assignees,
            doneDate: o.doneDate,
            href: '/eventOccurrences/' + o.uuid,
          };
        }),
      (x) => x.dateTime,
      (x) => x.occurrenceUuid,
    ),
    canUpdateFutureOccurrences,
    taskOptions: sortBy(taskOptions, ['text']),
    relatedCollections: getRelatedCollections(commonData.availableCollections, uuid),
    relatedRiskAssessments: getRelatedRiskAssessments(
      riskAssessmentsNotDeleted(state),
      uuid,
      currentParent + '/eventOccurrences/' + uuid,
    ),
    emergencyContent: isEmergencyContent(),
    newEventFromSuggestion: isNewEventFromSuggestion(state),
  };
}

function _checkedByTime(state: State, status, _comments, checkedBy, checkedTime) {
  let result = '';
  let d = '';
  if (checkedTime) {
    d = LocalDate.fromString(checkedTime).toStringForDisplay();
  }
  if (status !== 'NONE' && checkedBy !== '') {
    let name = '';
    if (!singleUserVersion(state)) {
      const names = employeesShortNamesAndMe(state).filter((item) => item.uuid === checkedBy);
      if (names) {
        name = t('content.doneByName', { name: names[0].name });
      }
    }
    result = t('content.donePrefix') + d + name;
  }
  return result;
}
